import { useEffect, useMemo, useRef, useState } from 'react';
import type { RehearsalOutput, RehearsalTurn, RehearseRequest } from '../api';

/**
 * A conversation with the flow being edited, before a customer has one.
 *
 * This is the piece the old Python builder had and the first version of this one did
 * not, and it is the difference between a builder you can use and a form you fill in
 * hopefully. Everything here runs through the **same interpreter that answers real
 * customers** — the server hands it a recorder instead of a WhatsApp client and
 * returns what it would have said — so there is no second implementation of the state
 * machine to drift out of step with the first.
 *
 * Three things it shows that the old one did not:
 *
 *   - **the calls**, in the trace beside the conversation, with the address actually
 *     built from the variables collected so far and the status that came back. When a
 *     step does not do what somebody expected, this is nearly always the answer.
 *   - **the variables**, as they fill in. A flow is a state machine over a bag of
 *     values and hiding the bag makes every condition a guess.
 *   - **what was not called.** The old builder ran every `pull` for real and faked
 *     every `push` — but the Carysil flow's *create a ticket* is a POST with
 *     `mode: pull`, so testing it opened a real complaint on a real account. Here the
 *     method decides, the choice is on screen, and anything simulated says so.
 *
 * It is drawn as WhatsApp rather than as a list of strings on purpose. What is being
 * checked is whether a customer can follow this — whether the menu is short enough to
 * read, whether the message is a wall of text, whether the button labels survive
 * WhatsApp's twenty characters. None of that is legible in a transcript, and all of it
 * is obvious in the shape the customer will actually see.
 */

type Bubble =
  | { side: 'bot'; output: RehearsalOutput; at: string }
  | { side: 'customer'; text: string; at: string }
  | { side: 'system'; text: string };

const now = () => new Date().toLocaleTimeString('en-IN', {
  hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
});

/**
 * WhatsApp's wallpaper, close enough to read as itself.
 *
 * Drawn rather than fetched: the page is served from the portal's own origin and a
 * remote image would be a request to somebody else's CDN on every render, for
 * decoration.
 */
const DOODLE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='84' height='84' viewBox='0 0 84 84'%3E%3Cg fill='none' stroke='%23000' stroke-opacity='.045' stroke-width='1.4'%3E%3Ccircle cx='14' cy='16' r='5'/%3E%3Cpath d='M38 10h10v9H38z'/%3E%3Cpath d='M62 22l5-8 5 8z'/%3E%3Cpath d='M10 44c4-5 9-5 13 0'/%3E%3Cpath d='M34 40h12M34 46h8'/%3E%3Ccircle cx='68' cy='46' r='6'/%3E%3Cpath d='M12 70h11v8H12z'/%3E%3Cpath d='M40 66l6 10h-12z'/%3E%3Cpath d='M62 70h12M62 76h7'/%3E%3C/g%3E%3C/svg%3E\")";

export function FlowEmulator({ definition, rehearse, disabled, title }: {
  definition: string;
  rehearse: (body: RehearseRequest) => Promise<RehearsalTurn>;
  disabled?: boolean;
  title?: string;
}) {
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [state, setState] = useState<string | null>(null);
  const [variables, setVariables] = useState<Record<string, unknown>>({});
  const [choices, setChoices] = useState<{ id: string; title: string }[]>([]);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [calls, setCalls] = useState<'none' | 'reads' | 'all'>('none');
  // Who the rehearsal is from. The live engine hands a flow the sender's number as
  // {{from_number}}; a flow that looks the customer up on message one needs a real
  // one to show the registered path, and the default — not a subscriber — shows the
  // stranger's path. Digits only, country code first, the way WhatsApp spells it.
  const [fromNumber, setFromNumber] = useState('919999999999');
  const [typed, setTyped] = useState('');
  const [panel, setPanel] = useState<'none' | 'vars' | 'trace'>('none');

  const thread = useRef<HTMLDivElement>(null);
  const latest = useRef(rehearse);
  latest.current = rehearse;

  useEffect(() => {
    thread.current?.scrollTo({ top: thread.current.scrollHeight, behavior: 'smooth' });
  }, [bubbles]);

  // A change to the definition invalidates the conversation in progress: the state it
  // is sitting in may not exist any more. Better to say so than to answer from a flow
  // that is no longer the one on screen.
  useEffect(() => {
    setStarted((was) => {
      if (was) setBubbles((b) => [...b, { side: 'system', text: 'The flow changed — restart to test it' }]);
      return false;
    });
    setChoices([]);
  }, [definition]);

  const apply = (turn: RehearsalTurn) => {
    const at = now();
    setBubbles((b) => [...b, ...turn.outputs.map((output) => ({ side: 'bot' as const, output, at }))]);
    setState(turn.state);
    setVariables(turn.variables ?? {});
    setEnded(turn.ended);

    const last = [...turn.outputs].reverse().find((o) => o.kind === 'list' || o.kind === 'buttons');
    setChoices(last?.rows?.map((r) => ({ id: r.id, title: r.title })) ?? []);

    if (turn.handedOff) {
      setBubbles((b) => [...b, { side: 'system', text: 'Handed over to a person — your routing rules run here' }]);
    } else if (turn.ended) {
      setBubbles((b) => [...b, { side: 'system', text: 'The conversation ended' }]);
    }
  };

  const turn = async (body: RehearseRequest, echo?: string) => {
    setBusy(true);
    if (echo !== undefined) setBubbles((b) => [...b, { side: 'customer', text: echo, at: now() }]);
    setChoices([]);
    try {
      apply(await latest.current({ ...body, definition, calls }));
    } catch (e) {
      setBubbles((b) => [...b, { side: 'system', text: e instanceof Error ? e.message : 'That did not run.' }]);
    } finally {
      setBusy(false);
    }
  };

  const restart = async () => {
    setBubbles([]);
    setVariables({});
    setEnded(false);
    setStarted(true);
    const from = fromNumber.replace(/\D/g, '');
    await turn({ definition, reset: true, variables: from ? { from_number: from } : undefined });
  };

  const send = async (kind: 'text' | 'selection', value: string, label?: string) => {
    if (!started || ended) return;
    await turn({ definition, state, variables, kind, value }, label ?? value);
  };

  const varRows = useMemo(
    () => Object.entries(variables).filter(([k]) => !k.startsWith('_')).sort(([a], [b]) => a.localeCompare(b)),
    [variables]);

  const trace = useMemo(
    () => bubbles.filter((b): b is Extract<Bubble, { side: 'bot' }> => b.side === 'bot' && b.output.kind === 'call'),
    [bubbles]);

  return (
    // A bounded column. Without the height the panel simply grew to fit the whole
    // conversation, `flex-1` had nothing to divide, and scrolling the transcript
    // scrolled the page — which is the bug: the flow you are editing slides away
    // while you read what the bot said.
    <div className="flex h-[34rem] min-h-0 flex-col overflow-hidden rounded-xl border border-(--color-line) bg-(--color-surface) xl:h-[calc(100vh-7rem)]">
      {/* --------------------------------------------------------- the phone -- */}
      <div className="flex items-center gap-3 bg-[#008069] px-4 py-2.5 text-white">
        <div className="grid size-9 shrink-0 place-items-center rounded-full bg-white/25 text-body font-semibold">
          {(title ?? 'Bot').slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-body font-medium">{title ?? 'Live test'}</p>
          <p className="text-small text-white/80">{busy ? 'typing…' : started ? 'online' : 'preview'}</p>
        </div>
        <button
          type="button"
          onClick={() => void restart()}
          disabled={disabled || busy}
          className="rounded-lg bg-white/20 px-3 py-1.5 text-small font-medium whitespace-nowrap disabled:opacity-50"
        >
          {started ? 'Restart' : 'Start'}
        </button>
      </div>

      <div
        ref={thread}
        className="pane-scroll min-h-0 flex-1 space-y-1.5 overflow-y-auto overscroll-contain px-3 py-3"
        style={{ backgroundColor: '#EFE7DE', backgroundImage: DOODLE }}
      >
        {bubbles.length === 0 && (
          <div className="grid h-full place-items-center px-6">
            <p className="rounded-lg bg-[#FFF5C4] px-4 py-3 text-center text-body text-[#54656f]">
              Press Start to have the conversation your customers will have.
            </p>
          </div>
        )}
        {bubbles.map((bubble, i) => <BubbleView key={i} bubble={bubble} />)}
        {busy && (
          <div className="flex justify-start">
            <span className="rounded-lg rounded-tl-none bg-white px-3 py-2 text-body text-[#667781] shadow-sm">…</span>
          </div>
        )}
      </div>

      {/* The options WhatsApp would show as taps. Kept out of the thread so a long
          menu cannot push the conversation off the top of a bounded panel. */}
      {choices.length > 0 && (
        <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto overscroll-contain border-t border-(--color-line) bg-(--color-surface) px-3 py-2.5">
          {choices.map((choice) => (
            <button
              key={choice.id}
              type="button"
              onClick={() => void send('selection', choice.id, choice.title)}
              disabled={busy}
              className="rounded-full border border-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand) disabled:opacity-40"
            >
              {choice.title}
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); const v = typed.trim(); if (v) { setTyped(''); void send('text', v); } }}
        className="flex shrink-0 items-center gap-2 border-t border-(--color-line) bg-(--color-surface) px-3 py-2.5"
      >
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={!started || ended || busy}
          placeholder={started ? (ended ? 'Restart to test again' : 'Type a message') : 'Press Start first'}
          className="min-w-0 flex-1 rounded-full border border-(--color-line) bg-(--color-paper) px-4 py-2 text-body disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!started || ended || busy || typed.trim().length === 0}
          aria-label="Send"
          className="grid size-9 shrink-0 place-items-center rounded-full bg-(--color-brand) text-white disabled:opacity-40"
        >
          <svg viewBox="0 0 24 24" className="size-4.5" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2z" /></svg>
        </button>
      </form>

      {/* ------------------------------------------------- what a customer can't see -- */}
      <div className="shrink-0 border-t border-(--color-line)">
        <div className="flex">
          {([['vars', 'Collected', varRows.length], ['trace', 'Calls', trace.length]] as const).map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              onClick={() => setPanel((p) => (p === key ? 'none' : key))}
              className={[
                'flex flex-1 items-center justify-center gap-2 px-3 py-2 text-small transition',
                panel === key ? 'bg-(--color-sunken) text-(--color-ink)' : 'text-(--color-muted)',
              ].join(' ')}
            >
              {label}
              <span className="rounded-full bg-(--color-sunken) px-1.5 font-mono">{count}</span>
            </button>
          ))}
        </div>

        {panel !== 'none' && (
          <div className="pane-scroll max-h-44 overflow-y-auto overscroll-contain border-t border-(--color-line) px-3 py-2.5">
            {panel === 'vars' && (
              varRows.length === 0
                ? <p className="text-small text-(--color-muted)">Nothing collected yet.</p>
                : (
                  <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-small">
                    {varRows.map(([key, value]) => (
                      <div key={key} className="contents">
                        <dt className="font-mono text-(--color-muted)">{key}</dt>
                        <dd className="truncate font-mono">{String(value ?? '')}</dd>
                      </div>
                    ))}
                  </dl>
                )
            )}

            {panel === 'trace' && (
              trace.length === 0
                ? <p className="text-small text-(--color-muted)">No calls yet.</p>
                : (
                  <ul className="flex flex-col gap-2">
                    {trace.map((b, i) => <CallLine key={i} output={b.output} />)}
                  </ul>
                )
            )}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-(--color-line) px-3 py-2.5">
        <label className="mb-2 flex flex-wrap items-center gap-2 text-small">
          <span className="text-(--color-muted)">Testing as</span>
          <input
            type="tel"
            inputMode="numeric"
            value={fromNumber}
            onChange={(e) => setFromNumber(e.target.value)}
            disabled={busy}
            placeholder="919999999999"
            aria-label="The number the rehearsal is from"
            className="w-40 rounded-lg border border-(--color-line) bg-(--color-paper) px-2.5 py-1.5 font-mono text-small"
          />
          <span className="text-(--color-muted)">{'{{from_number}}'} · applies on Start</span>
        </label>
        <label className="flex flex-wrap items-center gap-2 text-small">
          <span className="text-(--color-muted)">Calls to your systems</span>
          <select
            value={calls}
            onChange={(e) => setCalls(e.target.value as typeof calls)}
            className="rounded-lg border border-(--color-line) bg-(--color-paper) px-2.5 py-1.5 text-small"
          >
            <option value="none">Don&rsquo;t call anything</option>
            <option value="reads">Read for real (GET)</option>
            <option value="all">Everything for real</option>
          </select>
        </label>
        <p className="mt-1.5 text-small text-(--color-muted)">
          {calls === 'none' && 'Every call answers 200 with no data, so you are testing the shape of the flow.'}
          {calls === 'reads' && 'Lookups run against your real systems. Anything that could change something is not called.'}
          {calls === 'all' && (
            <span className="text-(--color-bad)">
              Writes run for real. A ticket created here is a real ticket on a real account.
            </span>
          )}
          {state ? ` Now at ${state === '__END__' ? 'the end' : state === '__HUMAN__' ? 'a person' : state}.` : ''}
        </p>
      </div>
    </div>
  );
}

/** One call, in the trace rather than in the thread — a customer never sees it. */
function CallLine({ output }: { output: RehearsalOutput }) {
  const failed = output.status === 0 || (output.status ?? 0) >= 400;
  return (
    <li className={[
      'rounded-lg border px-2.5 py-2 font-mono text-small',
      failed ? 'border-(--color-bad) text-(--color-bad)' : 'border-(--color-line) text-(--color-ink-soft)',
    ].join(' ')}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{output.method ?? '—'}</span>
        <span className="ml-auto whitespace-nowrap">
          {output.simulated ? 'not called' : output.status === 0 ? 'unreachable' : output.status}
        </span>
      </div>
      <p className="break-all">{output.url || output.action || 'no action'}</p>
      {output.note && <p className="mt-1 font-sans opacity-80">{output.note}</p>}
    </li>
  );
}

function BubbleView({ bubble }: { bubble: Bubble }) {
  if (bubble.side === 'system') {
    return (
      <div className="flex justify-center py-1">
        <p className="rounded-md bg-[#FFF5C4] px-3 py-1.5 text-center text-small text-[#54656f]">{bubble.text}</p>
      </div>
    );
  }

  if (bubble.side === 'customer') {
    return (
      <div className="flex justify-end">
        <div className="relative max-w-[85%] rounded-lg rounded-tr-none bg-[#D9FDD3] px-2.5 py-1.5 shadow-sm">
          <p className="text-body whitespace-pre-wrap text-[#111b21]">{bubble.text}</p>
          <p className="mt-0.5 flex items-center justify-end gap-1 text-small text-[#667781]">
            {bubble.at}
            <svg viewBox="0 0 18 12" className="w-3.5 text-[#53bdeb]" fill="currentColor" aria-label="read">
              <path d="M6.6 11.4L.9 5.7l1.4-1.4 4.3 4.3L15.7.5l1.4 1.4z" />
              <path d="M11.6 11.4L5.9 5.7l1.4-1.4 4.3 4.3L20.7.5l1.4 1.4z" transform="translate(-5)" opacity=".55" />
            </svg>
          </p>
        </div>
      </div>
    );
  }

  const o = bubble.output;

  // A call is not something the customer sees, so it does not go in the thread at all
  // — it is in the Calls panel underneath. Putting it in the conversation made the
  // preview stop being a preview of what a customer gets.
  if (o.kind === 'call') return null;

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-lg rounded-tl-none bg-white px-2.5 py-1.5 shadow-sm">
        {o.header && <p className="text-body font-semibold text-[#111b21]">{o.header}</p>}
        <p className="text-body whitespace-pre-wrap text-[#111b21]">{o.text ?? o.body}</p>
        {o.footer && <p className="mt-1 text-small text-[#8696a0]">{o.footer}</p>}

        {o.kind === 'list' && (
          <div className="mt-1.5 border-t border-[#e9edef] pt-1.5 text-center text-body font-medium text-[#00a884]">
            ☰ {o.button}
          </div>
        )}

        {o.kind === 'products' && (
          <div className="mt-1.5 flex flex-col gap-1">
            {(o.rows ?? []).map((row) => (
              <div key={row.id} className="flex items-center gap-2 rounded border border-[#e9edef] px-2 py-1.5 text-body text-[#111b21]">
                <span aria-hidden="true">🛍️</span>
                <span className="min-w-0 truncate">{row.title}</span>
              </div>
            ))}
            <div className="border-t border-[#e9edef] pt-1.5 text-center text-body font-medium text-[#00a884]">
              {(o.rows ?? []).length === 1 && o.rows?.[0]?.id !== 'catalogue' ? 'View' : 'View items'}
            </div>
          </div>
        )}

        {o.kind === 'buttons' && (
          <div className="mt-1.5 flex flex-col gap-1">
            {(o.rows ?? []).map((row) => (
              <div key={row.id} className="rounded border-t border-[#e9edef] py-1.5 text-center text-body text-[#00a884]">
                {row.title}
              </div>
            ))}
          </div>
        )}

        <p className="mt-0.5 text-right text-small text-[#667781]">{bubble.at}</p>
      </div>
    </div>
  );
}
